import { SensorType } from "common";
import React from "react";
import { PieChart } from "react-gifted-charts";
import { useTranslation } from "react-i18next";
import { Text, YStack, XStack, View, ColorTokens, Spinner } from "tamagui";
import { tokens } from "tamagui-config";

import { BorderedCard, BorderedCardProps } from "./BorderedCard";
import { EnergyCostChart } from "./EnergyCostChart";
import { GlowTriple } from "./GlowTriple";
import { LineChart } from "./LineChart";
import { WaterWave } from "./WaterWave";
import { CircularProgress } from "../components/common/CircularProgress";
import { useAppSync } from "../contexts/AppSyncContext";
import { useGetEnergySavingData } from "../hooks/electrical/useGetEnergySavingData";
import { useGetTopFiveElectricalEnergyUsage } from "../hooks/electrical/useGetTopFiveElectricalEnergyUsage";
import { useGetHVACFilterStatus } from "../hooks/hvac/useGetHVACFilterStatus";
import {
  useGetWaterQualityData,
  waterQualityColorTokens,
} from "../hooks/water/useGetWaterQualityData";
import { useHumidity } from "../hooks/water/useHumidity";

export const WaterQualityCard = ({
  ...rest
}: Omit<BorderedCardProps, "title" | "icon"> & {}) => {
  const { t } = useTranslation();
  const { data, loading, error } = useGetWaterQualityData();

  return (
    <BorderedCard title={t("water.waterQuality")} minHeight="282px" {...rest}>
      {loading ? (
        <YStack flex={1} justifyContent="center" alignItems="center">
          <Spinner size="large" />
        </YStack>
      ) : error ? (
        <YStack flex={1} justifyContent="center" alignItems="center">
          <Text color="$error">{t("common.error")}</Text>
        </YStack>
      ) : !data ? (
        <YStack flex={1} justifyContent="center" alignItems="center">
          <Text color="$gray11">{t("common.noData")}</Text>
        </YStack>
      ) : (
        <YStack gap="$3.5">
          <YStack
            alignItems="center"
            gap="$7"
            position="relative"
            justifyContent="space-between"
            flex={1}
            paddingTop="$6"
          >
            <View position="absolute" top={-10}>
              <GlowTriple
                gradientColors={
                  waterQualityColorTokens[data.color]
                    .backgroundGradients as ColorTokens[]
                }
                size={220}
              />
            </View>
            <WaterWave
              percentage={data.percentage}
              radius={60}
              gradientStart={waterQualityColorTokens[data.color].gradientStart}
              gradientEnd={waterQualityColorTokens[data.color].gradientEnd}
            />
            <Text color="$color">{data.label}</Text>
          </YStack>
        </YStack>
      )}
    </BorderedCard>
  );
};

export const HVACFilterStatusCard = () => {
  const { data, loading, error } = useGetHVACFilterStatus();
  const { t } = useTranslation();

  return (
    <BorderedCard title={t("air.filterStatus")} minHeight="346px">
      <XStack
        height="100%"
        paddingTop="$3"
        paddingHorizontal="$10"
        gap="$12"
        justifyContent="center"
        alignItems="center"
      >
        {loading ? (
          <Spinner size="large" />
        ) : error ? (
          <Text color="$error">{t("common.error")}</Text>
        ) : !data ? (
          <Text color="$gray11">{t("common.noData")}</Text>
        ) : (
          <CircularProgress
            value={data.value}
            label={t("hvac.main")}
            progressColor={data.color}
          />
        )}
      </XStack>
    </BorderedCard>
  );
};

const Dot = ({ color }: { color: string }) => (
  <YStack
    width={12}
    height={12}
    borderRadius={6}
    backgroundColor={color}
    marginRight={4}
  />
);

export interface EnergySavingContentProps {
  dailyCost: string;
  lastWeekComparison: number;
  averageCost: number;
  dailyCosts: { value: number; label: string }[];
}

const TOTAL_NUMBER_OF_DAYS_TO_FETCH = 14;

export const EnergySavingContent: React.FC = () => {
  const { t } = useTranslation();
  const { data: energySavings } = useGetEnergySavingData(
    TOTAL_NUMBER_OF_DAYS_TO_FETCH,
  );

  const defaults = {
    dailyCost: 0,
    lastWeekComparison: "",
    averageCost: 0,
    dailyCosts: [],
  };

  const { dailyCost, lastWeekComparison, averageCost, dailyCosts } =
    energySavings || defaults;

  return (
    <BorderedCard title={t("electrical.energySavings")} minHeight="291px">
      <YStack height="100%" justifyContent="space-between">
        <YStack>
          <XStack>
            <Text
              fontFamily="$heading"
              fontSize="$5"
              fontWeight="700"
              color="$white12"
            >
              {dailyCost}
            </Text>
            <XStack flex={1} />
            <XStack alignItems="center" gap="$2">
              <Dot color={tokens.color.hgPrimary} />
              <Text fontSize="$4" color="$white12">
                {t("electrical.dailyCost")}
              </Text>
              <Dot color={tokens.color.hgSecondary} />
              <Text fontSize="$4" color="$white12">
                {t("electrical.average")}
              </Text>
            </XStack>
          </XStack>
          {lastWeekComparison !== "" && (
            <XStack gap="$2">
              {parseFloat(lastWeekComparison) < 0 ? (
                <Text color="$successGreen" fontSize="$4.5" fontWeight="700">
                  {t("electrical.percentLess", {
                    percent: Math.abs(+lastWeekComparison),
                  })}
                </Text>
              ) : (
                <Text color="$warningRed" fontSize="$4.5" fontWeight="700">
                  {t("electrical.percentMore", { percent: lastWeekComparison })}
                </Text>
              )}
              <Text fontSize="$4.5" color="$white12">
                {t("electrical.thanLastWeek")}
              </Text>
            </XStack>
          )}
        </YStack>

        <EnergyCostChart dailyCosts={dailyCosts} averageCost={averageCost} />
      </YStack>
    </BorderedCard>
  );
};

export const TopLiveElectricalUsageCard: React.FC = () => {
  const { t } = useTranslation();
  const { devices } = useGetTopFiveElectricalEnergyUsage();
  const data = devices;

  const colorPalette = [
    tokens.color.topUsageColor1,
    tokens.color.topUsageColor2,
    tokens.color.topUsageColor3,
    tokens.color.topUsageColor4,
    tokens.color.topUsageColor5,
  ];
  const maxValue = Math.max(...data.map((device) => device.value));
  const chartData = data.map((device, index) => ({
    value: device.value,
    color: colorPalette[index],
    text: `${device.value}%`,
  }));

  return (
    <BorderedCard title={t("electrical.topEnergyUsage")} minHeight="346px">
      <XStack
        paddingVertical="$7"
        gap="$8"
        alignItems="center"
        justifyContent="center"
      >
        <View
          width={200}
          height={200}
          alignItems="center"
          justifyContent="center"
        >
          <PieChart
            data={chartData}
            donut
            innerRadius={60}
            radius={100}
            innerCircleColor={tokens.color.darkBackgroundGradient}
            centerLabelComponent={() => (
              <Text fontSize="$6" fontWeight="bold">
                {maxValue}%
              </Text>
            )}
          />
        </View>
        <YStack gap="$3" alignSelf="center">
          {data.map((item, index) => (
            <XStack key={index} alignItems="center" gap="$2">
              <View
                width={8}
                height={8}
                borderRadius={5}
                backgroundColor={colorPalette[index]}
              />
              <Text maxWidth={120}>{item.name}</Text>
              <Text
                backgroundColor={tokens.color.pillBackground}
                paddingHorizontal={5}
                paddingVertical={1}
                borderRadius={5}
              >
                {item.value}%
              </Text>
            </XStack>
          ))}
        </YStack>
      </XStack>
    </BorderedCard>
  );
};

export const HumidityCard: React.FC = () => {
  const { t } = useTranslation();
  const { sensors } = useAppSync();

  const humiditySensor = React.useMemo(() => {
    if (!sensors || sensors.length === 0) {
      return null;
    }

    return sensors.find(
      (s) =>
        s.type === SensorType.LEAK_DETECTOR ||
        s.type === SensorType.HVAC ||
        s.type === SensorType.AIR_QUALITY,
    );
  }, [sensors]);

  const {
    humidityData,
    loading: humidityLoading,
    error: humidityError,
  } = useHumidity(humiditySensor?.id);
  const hasData = humidityData && humidityData.length > 0;
  const hasError = hasData && humidityData[humidityData.length - 1].error;

  return (
    <BorderedCard title={t("water.humidity")} minHeight="287px">
      {humidityLoading ? (
        <YStack flex={1} justifyContent="center" alignItems="center">
          <Spinner size="large" />
        </YStack>
      ) : humidityError ? (
        <YStack flex={1} justifyContent="center" alignItems="center">
          <Text color="$error">{t("common.error")}</Text>
        </YStack>
      ) : (
        <YStack gap="$5" justifyContent="space-between" height="100%">
          <YStack flexGrow={1}>
            <XStack alignItems="center" gap="$4">
              <Text fontWeight="700" fontSize="$7.5">
                {humidityData.length > 0
                  ? `${Math.min(humidityData[humidityData.length - 1].value, 100).toFixed(1)}%`
                  : "--"}
              </Text>
              <Text fontSize="$7.5">{t("water.humidity")}</Text>
            </XStack>
            {hasError && (
              <Text
                fontSize="$4"
                fontWeight="700"
                color={tokens.color.warningRed}
              >
                {t("water.leakDetected")}
              </Text>
            )}
          </YStack>
          <LineChart
            data={humidityData}
            unit={"%"}
            minValue={0}
            maxValue={100}
            chartHeight={110}
            noOfSections={2}
            color={tokens.color.humidityGlowCritical}
            lineGradientStartColor={tokens.color.humidityGlowCritical}
            startFillColor={tokens.color.humidityGlowCritical}
            lineGradientEndColor={tokens.color.humidityGlowCritical}
            endFillColor={tokens.color.humidityGlowCritical}
          />
        </YStack>
      )}
    </BorderedCard>
  );
};
